import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftIcon, ArrowRightWhiteIcon, CloseIcon, EyeIcon, EyeSlashIcon } from '../../components/icons';
import { apiService } from '../../services/apiService';
import { clearReturnAfterAuth, ssoRedirectUrls } from '../../utils/returnAfterAuth';
import { useSignIn, useClerk, useSession } from '@clerk/clerk-react';
import { trackClick } from '../../utils/analytics';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../../components/InlineAlert';
import { ForgotPasswordModal } from '../../components/modals/ForgotPasswordModal';

// TypeScript declaration for dataLayer
declare global {
    interface Window {
        dataLayer?: any[];
    }
}

// GTM Event Tracking Helper
const trackGTMEvent = (eventName: string, eventData?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...eventData
        });
    }
};

// Feature Flag: Enable/Disable LinkedIn OAuth button
// Set to true to show "Continue with LinkedIn" button, false to hide it.
// Hidden for now — the handler, the Clerk oauth_linkedin_oidc strategy and the
// OAuth callback in index.tsx are all left wired, so flipping this back to true
// restores the flow with no other change.
const ENABLE_LINKEDIN_AUTH = false;

// Feature Flag: Enable/Disable Google OAuth button
const ENABLE_GOOGLE_AUTH = true;

interface LoginModalProps {
    onClose: () => void;
    /** `method`: how they signed in (password | otp), for the return-to-page
     *  tracking of the public tech pages. */
    onLogin: (email: string, method?: 'password' | 'otp') => void;
    onSignUpClick: () => void;
    /** Called once a forgotten password has actually been reset. The modal
     *  runs the whole reset itself and then returns to the password sign-in,
     *  so this is for tracking only — don't close or open anything here. */
    onForgotPasswordSuccess?: () => void;
    /** A heading for why the modal opened, e.g. "Sign up to Contact
     *  University" from a public tech page (default: the Cosmos welcome). */
    heading?: string;
    subheading?: string;
    /** Opened by a public tech / university page (PublicShell): Google /
     *  LinkedIn come back to that page (utils/returnAfterAuth). Anywhere else
     *  any pending page is forgotten, so an old one cannot hijack this login. */
    returnAfterAuth?: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PasswordInput: React.FC<{value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void}> = ({ value, onChange }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="flex flex-col items-start gap-1.5 w-full">
            <label className="text-grayscale-1100 font-grotesk text-xs">Password</label>
            <div className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md border-primary-300 border-[0.5px] bg-white">
                <input
                    type={show ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={value}
                    onChange={onChange}
                    className="flex-1 bg-transparent text-grayscale-1300 font-grotesk text-sm focus:outline-none"
                />
                <button type="button" onClick={() => setShow(!show)}>
                    {show ? <EyeIcon /> : <EyeSlashIcon />}
                </button>
            </div>
        </div>
    );
};


const LoginModal: React.FC<LoginModalProps> = ({ onClose, onLogin, onSignUpClick, onForgotPasswordSuccess, heading, subheading, returnAfterAuth }) => {
    // Not a public page's modal: no return to an older page.
    useEffect(() => { if (!returnAfterAuth) clearReturnAfterAuth(); }, [returnAfterAuth]);
    const { signIn, isLoaded: isSignInLoaded } = useSignIn();
    const clerk = useClerk();
    const { session, isLoaded: isSessionLoaded } = useSession();
    
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailStatus, setEmailStatus] = useState<'idle' | 'invalid' | 'checking' | 'approved' | 'unregistered' | 'moved'>('idle');
    const [movedMessage, setMovedMessage] = useState('');
    const showPasswordField = emailStatus !== 'unregistered';
    const [isForgotPasswordFlow, setIsForgotPasswordFlow] = useState(false);
    // Forgot-password: the one-time token the reset code returned; while set,
    // the "Set New Password" step is shown. `notice` confirms the reset.
    const [resetToken, setResetToken] = useState<string | null>(null);
    const [notice, setNotice] = useState('');
    const [otp, setOtp] = useState(['', '', '', '']);
    const [timer, setTimer] = useState(25);
    const [otpId, setOtpId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const inputRefs = [
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null)
    ];

    // Track modal open
    useEffect(() => {
        trackGTMEvent('login_modal_opened', {
            component: 'LoginModal'
        });
    }, []);

    useEffect(() => {
        const checkEmail = async () => {
            const lowerEmail = email.trim().toLowerCase();
            setErrorMessage('');
            
            if (lowerEmail === '') {
                setEmailStatus('idle');
                return;
            }
            
            if (!emailRegex.test(lowerEmail)) {
                setEmailStatus('invalid');
                trackGTMEvent('login_email_invalid', {
                    component: 'LoginModal',
                    email: lowerEmail
                });
                return;
            }

            setEmailStatus('checking');
            try {
                const response = await apiService.checkEmail(lowerEmail);
                if (response.success && response.data) {
                    if (response.data.exists) {
                        setEmailStatus('approved');
                        trackGTMEvent('login_email_approved', {
                            component: 'LoginModal',
                            email: lowerEmail
                        });
                    } else if (response.data.moved) {
                        // An admin moved this account to another address: say so
                        // rather than offering a new, empty account.
                        setMovedMessage(response.data.message || '');
                        setEmailStatus('moved');
                    } else {
                        setEmailStatus('unregistered');
                        trackGTMEvent('login_email_unregistered', {
                            component: 'LoginModal',
                            email: lowerEmail
                        });
                    }
                } else {
                    setEmailStatus('unregistered');
                }
            } catch (error) {
                console.error('Email check failed:', error);
                setEmailStatus('unregistered');
            }
        };

        const timeoutId = setTimeout(checkEmail, 500);
        return () => clearTimeout(timeoutId);
    }, [email]);
    
    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (step === 'otp' && timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [step, timer]);

    // FIXED: LinkedIn OAuth handler with session check
    const handleLinkedInLogin = async () => {
        trackGTMEvent('login_linkedin_clicked', {
            component: 'LoginModal'
        });
        
        // Check if Clerk is loaded
        if (!isSignInLoaded || !isSessionLoaded) {
            setErrorMessage('Authentication service is loading. Please try again.');
            return;
        }
        
        if (!signIn) {
            setErrorMessage('Authentication service not available. Please refresh the page.');
            return;
        }
        
        setIsLinkedInLoading(true);
        setErrorMessage('');
        
        try {
            // ============================================
            // FIX: Check if there's an existing session
            // If user is already signed in to Clerk, sign them out first
            // ============================================
            if (session) {
                console.log('Existing Clerk session detected, signing out first...');
                try {
                    await clerk.signOut();
                    // Small delay to ensure sign out completes
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (signOutError) {
                    console.warn('Sign out warning:', signOutError);
                    // Continue anyway - the session might already be invalid
                }
            }
            
            // Set login mode flag so ClerkAuthHandler knows to check if account exists
            sessionStorage.setItem('clerk_login_mode', 'true');
            // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".
            sessionStorage.removeItem('clerk_signup_mode');
            
            // Now proceed with OAuth
            await signIn.authenticateWithRedirect({
                strategy: 'oauth_linkedin_oidc', // Try 'oauth_linkedin' if this doesn't work
                // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
            });
        } catch (error: any) {
            console.error('Clerk OAuth error:', error);
            
            // ============================================
            // FIX: Handle "already signed in" error specifically
            // ============================================
            if (error.errors) {
                const clerkError = error.errors[0];
                
                if (clerkError?.code === 'session_exists' || 
                    clerkError?.message?.includes('already signed in')) {
                    // Force sign out and retry
                    try {
                        console.log('Session exists error - forcing sign out...');
                        await clerk.signOut();
                        
                        // Clear any lingering cookies/storage
                        // Clerk stores session in cookies, this helps ensure they're cleared
                        document.cookie.split(";").forEach((c) => {
                            if (c.includes('__clerk') || c.includes('__session')) {
                                document.cookie = c
                                    .replace(/^ +/, "")
                                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                            }
                        });
                        
                        // Wait a moment then retry
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        // Re-set login mode flag for retry
                        sessionStorage.setItem('clerk_login_mode', 'true');
                        // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".
                        sessionStorage.removeItem('clerk_signup_mode');
                        
                        // Retry the OAuth
                        await signIn.authenticateWithRedirect({
                            strategy: 'oauth_linkedin_oidc',
                            // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                            ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
                        });
                        return; // Success on retry
                    } catch (retryError) {
                        console.error('Retry after sign out failed:', retryError);
                        sessionStorage.removeItem('clerk_login_mode');
                        setErrorMessage('Session conflict. Please refresh the page and try again.');
                    }
                } else if (clerkError?.code === 'form_identifier_not_found') {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('LinkedIn login is not properly configured. Please contact support.');
                } else if (clerkError?.code === 'external_account_not_found') {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('No account found with this LinkedIn profile. Please register first.');
                } else {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage(clerkError?.message || 'LinkedIn login failed. Please try again.');
                }
            } else if (error.message?.includes('already signed in')) {
                // Handle string error message
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    // Re-set login mode flag for retry
                    sessionStorage.setItem('clerk_login_mode', 'true');
                    // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".
                    sessionStorage.removeItem('clerk_signup_mode');
                    
                    await signIn.authenticateWithRedirect({
                        strategy: 'oauth_linkedin_oidc',
                        // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                        ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
                    });
                    return;
                } catch (retryError) {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('Session conflict. Please refresh the page and try again.');
                }
            } else {
                sessionStorage.removeItem('clerk_login_mode');
                setErrorMessage('LinkedIn login failed. Please try again or use email/password.');
            }
            
            trackGTMEvent('login_linkedin_error', {
                component: 'LoginModal',
                error: error.message || 'Unknown error'
            });
        } finally {
            setIsLinkedInLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        trackGTMEvent('login_google_clicked', { component: 'LoginModal' });

        if (!isSignInLoaded || !isSessionLoaded) {
            setErrorMessage('Authentication service is loading. Please try again.');
            return;
        }
        if (!signIn) {
            setErrorMessage('Authentication service not available. Please refresh the page.');
            return;
        }

        setIsGoogleLoading(true);
        setErrorMessage('');

        try {
            if (session) {
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (signOutError) {
                    console.warn('Sign out warning:', signOutError);
                }
            }

            sessionStorage.setItem('clerk_login_mode', 'true');

            // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".

            sessionStorage.removeItem('clerk_signup_mode');

            await signIn.authenticateWithRedirect({
                strategy: 'oauth_google',
                // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                ...ssoRedirectUrls('google', !!returnAfterAuth),
            });
        } catch (error: any) {
            console.error('Clerk Google OAuth error:', error);

            if (error.errors) {
                const clerkError = error.errors[0];

                if (clerkError?.code === 'session_exists' ||
                    clerkError?.message?.includes('already signed in')) {
                    try {
                        await clerk.signOut();
                        document.cookie.split(";").forEach((c) => {
                            if (c.includes('__clerk') || c.includes('__session')) {
                                document.cookie = c
                                    .replace(/^ +/, "")
                                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                            }
                        });
                        await new Promise(resolve => setTimeout(resolve, 500));
                        sessionStorage.setItem('clerk_login_mode', 'true');
                        // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".
                        sessionStorage.removeItem('clerk_signup_mode');
                        await signIn.authenticateWithRedirect({
                            strategy: 'oauth_google',
                            // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                            ...ssoRedirectUrls('google', !!returnAfterAuth),
                        });
                        return;
                    } catch (retryError) {
                        sessionStorage.removeItem('clerk_login_mode');
                        setErrorMessage('Session conflict. Please refresh the page and try again.');
                    }
                } else if (clerkError?.code === 'form_identifier_not_found') {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('Google login is not properly configured. Please contact support.');
                } else if (clerkError?.code === 'external_account_not_found') {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('No account found with this Google profile. Please register first.');
                } else {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage(clerkError?.message || 'Google login failed. Please try again.');
                }
            } else if (error.message?.includes('already signed in')) {
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 500));
                    sessionStorage.setItem('clerk_login_mode', 'true');
                    // A login, not a sign-up: a stale sign-up flag must not reopen the sign-up modal at "/".
                    sessionStorage.removeItem('clerk_signup_mode');
                    await signIn.authenticateWithRedirect({
                        strategy: 'oauth_google',
                        // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                        ...ssoRedirectUrls('google', !!returnAfterAuth),
                    });
                    return;
                } catch (retryError) {
                    sessionStorage.removeItem('clerk_login_mode');
                    setErrorMessage('Session conflict. Please refresh the page and try again.');
                }
            } else {
                sessionStorage.removeItem('clerk_login_mode');
                setErrorMessage('Google login failed. Please try again or use email/password.');
            }

            trackGTMEvent('login_google_error', {
                component: 'LoginModal',
                error: error.message || 'Unknown error'
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleNextClick = async () => {
        if (emailStatus === 'approved') {
            setIsLoading(true);
            setErrorMessage('');
            
            const normalizedEmail = email.trim().toLowerCase();
            trackGTMEvent('login_otp_send_attempt', {
                component: 'LoginModal',
                email: normalizedEmail,
                login_method: 'otp'
            });
            
            try {
                const response = await apiService.sendOTP(normalizedEmail, 'login');
                if (response.success && response.data) {
                    setOtpId(response.data.otp_id);
                    setStep('otp');
                    setTimer(response.data.ttl_sec || 25);
                    setIsForgotPasswordFlow(false);
                    trackGTMEvent('login_otp_sent', {
                        component: 'LoginModal',
                        email: normalizedEmail
                    });
                } else {
                    setErrorMessage(friendlyAuthError(response.error, 'Failed to send OTP. Please try again.'));
                    trackGTMEvent('login_otp_send_error', {
                        component: 'LoginModal',
                        email: normalizedEmail,
                        error: response.error || 'Unknown error'
                    });
                }
            } catch (error) {
                setErrorMessage('Network error. Please check your connection and try again.');
                trackGTMEvent('login_otp_send_network_error', {
                    component: 'LoginModal',
                    email: normalizedEmail
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleSignUp = () => {
        trackGTMEvent('login_sign_up_clicked', {
            component: 'LoginModal',
            email: email.trim().toLowerCase()
        });
        onSignUpClick();
    };
    
    const handleLogin = async () => {
        if (!password.trim()) return;
        
        trackClick('pre_login_submit_login');
        setIsLoading(true);
        setErrorMessage('');
        setNotice('');
        
        const normalizedEmail = email.trim().toLowerCase();
        trackGTMEvent('login_password_attempt', {
            component: 'LoginModal',
            email: normalizedEmail,
            login_method: 'password'
        });
        
        try {
            const response = await apiService.loginWithPassword(normalizedEmail, password);
            if (response.success && response.data) {
                apiService.setStoredAccessToken(response.data.access_token);
                apiService.setStoredRefreshToken(response.data.refresh_token);
                apiService.setStoredUserId(response.data.user_id);

                trackGTMEvent('login_success', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    login_method: 'password'
                });

                onLogin(normalizedEmail, 'password');
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Invalid credentials. Please try again.'));
                trackGTMEvent('login_failure', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    login_method: 'password',
                    error: response.error || 'Invalid credentials'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('login_network_error', {
                component: 'LoginModal',
                email: normalizedEmail,
                login_method: 'password'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < 3) {
            inputRefs[index + 1].current?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handleForgotPasswordClick = async () => {
        setIsLoading(true);
        setErrorMessage('');
        
        const normalizedEmail = email.trim().toLowerCase();
        trackGTMEvent('login_forgot_password_clicked', {
            component: 'LoginModal',
            email: normalizedEmail
        });
        
        try {
            const response = await apiService.sendOTP(normalizedEmail, 'password_reset');
            if (response.success && response.data) {
                setOtpId(response.data.otp_id);
                setIsForgotPasswordFlow(true);
                setStep('otp');
                setTimer(response.data.ttl_sec || 25);
                trackGTMEvent('login_password_reset_otp_sent', {
                    component: 'LoginModal',
                    email: normalizedEmail
                });
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Failed to send password reset OTP. Please try again.'));
                trackGTMEvent('login_password_reset_otp_error', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    error: response.error || 'Unknown error'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('login_password_reset_network_error', {
                component: 'LoginModal',
                email: normalizedEmail
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSuccess = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 4) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        const normalizedEmail = email.trim().toLowerCase();
        const purpose = isForgotPasswordFlow ? 'password_reset' : 'login';
        trackGTMEvent('login_otp_verify_attempt', {
            component: 'LoginModal',
            email: normalizedEmail,
            purpose: purpose
        });
        
        try {
            const response = await apiService.verifyOTP(normalizedEmail, otpString, purpose);
            
            if (response.success && response.data) {
                if (isForgotPasswordFlow) {
                    const token = response.data.reset_token;
                    if (!token) {
                        setErrorMessage('Something went wrong. Please go back and request a new code.');
                        return;
                    }
                    trackGTMEvent('login_password_reset_code_verified', {
                        component: 'LoginModal',
                        email: normalizedEmail
                    });
                    setResetToken(token);
                } else {
                    apiService.setStoredAccessToken(response.data.access_token);
                    apiService.setStoredRefreshToken(response.data.refresh_token);
                    apiService.setStoredUserId(response.data.user_id);
                    
                    trackGTMEvent('login_success', {
                        component: 'LoginModal',
                        email: normalizedEmail,
                        login_method: 'otp'
                    });
                    
                    onLogin(normalizedEmail, 'otp');
                }
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Invalid OTP. Please try again.'));
                trackGTMEvent('login_otp_verify_failure', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    purpose: purpose,
                    error: response.error || 'Invalid OTP'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('login_otp_verify_network_error', {
                component: 'LoginModal',
                email: normalizedEmail,
                purpose: purpose
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!otpId) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        const normalizedEmail = email.trim().toLowerCase();
        trackGTMEvent('login_otp_resend_attempt', {
            component: 'LoginModal',
            email: normalizedEmail,
            is_password_reset: isForgotPasswordFlow
        });
        
        try {
            const response = await apiService.resendOTP(otpId);
            if (response.success && response.data) {
                setTimer(response.data.ttl_sec || 25);
                trackGTMEvent('login_otp_resent', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    is_password_reset: isForgotPasswordFlow
                });
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Failed to resend OTP. Please try again.'));
                trackGTMEvent('login_otp_resend_error', {
                    component: 'LoginModal',
                    email: normalizedEmail,
                    error: response.error || 'Unknown error'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('login_otp_resend_network_error', {
                component: 'LoginModal',
                email: normalizedEmail
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    const isOtpValid = otp.every(digit => digit !== '');

    const handleBackFromOTP = () => {
        trackGTMEvent('login_back_from_otp', {
            component: 'LoginModal',
            email: email.trim().toLowerCase(),
            is_password_reset: isForgotPasswordFlow
        });
        setStep('email');
        setIsForgotPasswordFlow(false);
    }

    // Back to the password sign-in, e-mail kept, after a reset or a cancel.
    const leaveReset = () => {
        setResetToken(null);
        setIsForgotPasswordFlow(false);
        setStep('email');
        setOtp(['', '', '', '']);
        setPassword('');
        setErrorMessage('');
    };

    const handleResetDone = () => {
        trackGTMEvent('login_password_reset_success', {
            component: 'LoginModal',
            email: email.trim().toLowerCase()
        });
        leaveReset();
        setNotice('Your password has been updated. Log in with your new password.');
        onForgotPasswordSuccess?.();
    };

    const handleModalClose = (reason?: 'back' | 'outside' | 'close_button') => {
        trackGTMEvent('login_modal_closed', {
            component: 'LoginModal',
            step: step,
            email_status: emailStatus,
            close_reason: reason || 'unknown'
        });
        onClose();
    }

    if (resetToken) {
        return (
            <ForgotPasswordModal
                isOpen
                resetToken={resetToken}
                resetEmail={email.trim().toLowerCase()}
                onClose={leaveReset}
                onSetPassword={handleResetDone}
            />
        );
    }

    if (step === 'otp') {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60] font-grotesk p-4">
                <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 relative">
                    <button onClick={() => handleModalClose('close_button')} className="absolute top-4 right-4 p-2.5 rounded-full bg-grayscale-1200 text-white hover:bg-grayscale-1100 shadow-md">
                        <CloseIcon />
                    </button>
                    <div className="text-center mb-6">
                        <h2 className="text-xl font-medium text-grayscale-1400 font-grotesk">Continue your Cosmos journey</h2>
                        <p className="text-sm text-grayscale-1100 mt-1">Almost there!</p>
                    </div>
                    
                    <div className="w-full max-w-sm mx-auto flex flex-col items-center gap-6">
                        <div className="w-full">
                            <label className="block text-grayscale-1100 mb-4 text-left font-grotesk text-sm font-medium leading-tight">We have sent an OTP to {email}</label>
                            <div className="flex justify-center gap-4">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={inputRefs[index]}
                                        type="text"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className="w-14 h-14 text-center text-xl border border-primary-300 rounded-lg bg-white text-grayscale-1300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="text-center text-sm text-grayscale-1100 font-grotesk">
                            {timer > 0 ? (
                                <>Resend OTP in <strong>{timer < 10 ? `0${timer}` : timer}</strong> seconds</>
                            ) : (
                                <button 
                                    onClick={handleResendOtp} 
                                    disabled={isLoading}
                                    className="font-bold text-grayscale-1100 underline disabled:opacity-50"
                                >
                                    {isLoading ? 'Resending...' : 'Resend OTP'}
                                </button>
                            )}
                        </div>

                        {errorMessage && (
                            <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                        )}

                        <div className="flex items-center gap-4 w-full">
                            <button onClick={handleBackFromOTP} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-grayscale-1400 bg-white border border-primary-600 rounded-full hover:bg-primary-100 transition-colors shadow-custom-light">
                                <ArrowLeftIcon />
                                Back
                            </button>
                            <button
                                onClick={handleOtpSuccess}
                                disabled={!isOtpValid || isLoading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-full transition-colors shadow-custom-light disabled:bg-primary-300 disabled:cursor-not-allowed bg-primary-600 hover:bg-primary-700"
                            >
                                <span>{isLoading ? 'Verifying...' : 'Next'}</span>
                                {!isLoading && <ArrowRightWhiteIcon />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[60] font-grotesk p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-4 sm:p-6 md:p-8 relative my-auto">
                <button onClick={() => handleModalClose('close_button')} className="absolute top-4 right-4 p-2.5 rounded-full bg-grayscale-1200 text-white hover:bg-grayscale-1100 shadow-md">
                    <CloseIcon />
                </button>
                <div className="text-center mb-8">
                    <h2 className="text-xl font-medium text-grayscale-1400 font-grotesk">{heading || 'Start your Cosmos journey'}</h2>
                    <p className="text-sm text-grayscale-1100 mt-2">
                        {subheading || 'Login with your registered email'}
                    </p>
                </div>

                <div className="w-full max-w-sm mx-auto flex flex-col items-center gap-4">
                    {/* Google OAuth Button */}
                    {ENABLE_GOOGLE_AUTH && (
                        <div className="w-full">
                            <button
                                onClick={handleGoogleLogin}
                                disabled={isGoogleLoading || !isSignInLoaded}
                                className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm font-medium text-grayscale-1400 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-custom-light disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isGoogleLoading ? (
                                    <span>Connecting...</span>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                        </svg>
                                        Continue with Google
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* LinkedIn OAuth Button */}
                    {ENABLE_LINKEDIN_AUTH && (
                        <div className="w-full">
                            <button
                                onClick={handleLinkedInLogin}
                                disabled={isLinkedInLoading || !isSignInLoaded}
                                className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm font-medium text-white bg-[#0077B5] rounded-full hover:bg-[#006399] transition-colors shadow-custom-light disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLinkedInLoading ? (
                                    <span>Connecting...</span>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                        </svg>
                                        Continue with LinkedIn
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Divider - Shown when social auth buttons are enabled */}
                    {(ENABLE_LINKEDIN_AUTH || ENABLE_GOOGLE_AUTH) && (
                        <div className="w-full flex items-center gap-3">
                            <div className="flex-1 h-[0.5px] bg-primary-300"></div>
                            <span className="text-xs text-grayscale-1100 font-grotesk">or</span>
                            <div className="flex-1 h-[0.5px] bg-primary-300"></div>
                        </div>
                    )}

                    <div className="w-full">
                        <div className="flex flex-col items-start gap-1 w-full">
                            <label htmlFor="email" className="text-grayscale-1100 font-grotesk text-xs">
                                Enter registered Email ID
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                placeholder="Enter your email"
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full h-11 py-3 px-2.5 text-sm border-[0.5px] border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 bg-white"
                            />
                            <div className="h-4 mt-1">
                                {emailStatus === 'checking' && (
                                    <p className="text-xs text-gray-500">Checking email...</p>
                                )}
                                {emailStatus === 'approved' && (
                                    <p className="text-xs text-primary-700">This email is registered with us.</p>
                                )}
                                {emailStatus === 'unregistered' && (
                                    <p className="text-xs text-red-500">This email is not registered. Please sign up.</p>
                                )}
                                {emailStatus === 'moved' && (
                                    <p className="text-xs text-red-500">{movedMessage || 'This e-mail address is no longer used to sign in — the account moved to a different address. Sign in with your current e-mail address, or write to info@universitytech.io.'}</p>
                                )}
                                {emailStatus === 'invalid' && email.length > 0 && (
                                    <p className="text-xs text-red-500">Please enter a valid email address.</p>
                                )}
                            </div>
                        </div>
                        
                        {showPasswordField && (
                           <div className="mt-2 w-full flex flex-col items-start">
                             <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                             {emailStatus === 'approved' && (
                                 <button onClick={handleForgotPasswordClick} className="text-sm text-primary-600 underline mt-1 font-grotesk">
                                    Forgot Password?
                                 </button>
                             )}
                           </div>
                        )}
                    </div>

                    {errorMessage && (
                        <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                    )}
                    {notice && !errorMessage && (
                        <InlineAlert variant="success" className="mt-4">{notice}</InlineAlert>
                    )}

                    <div className="flex items-center gap-4 w-full max-w-sm mx-auto mt-4">
                        <button 
                            onClick={handleNextClick}
                            disabled={emailStatus !== 'approved' || isLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-grayscale-1400 bg-white border border-primary-600 rounded-full hover:bg-primary-100 transition-colors shadow-custom-light disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                        >
                            <span>{isLoading ? 'Loading...' : 'Login with OTP'}</span>
                        </button>
                        {emailStatus === 'unregistered' ? (
                            <button
                                onClick={handleSignUp}
                                disabled={isLoading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors shadow-custom-light disabled:bg-primary-200 disabled:cursor-not-allowed"
                            >
                                <span>Sign Up</span>
                                {!isLoading && <ArrowRightWhiteIcon />}
                            </button>
                        ) : (
                            <button
                                onClick={handleLogin}
                                disabled={!password.trim() || isLoading || emailStatus !== 'approved'}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors shadow-custom-light disabled:bg-primary-200 disabled:cursor-not-allowed"
                            >
                                <span>{isLoading ? 'Logging in...' : 'Login'}</span>
                                {!isLoading && <ArrowRightWhiteIcon />}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;